import LogRocket from 'logrocket';
import * as Sentry from '@sentry/react';
import setupLogRocketReact from 'logrocket-react';
import { PostHog } from 'posthog-js/react';
import configuration from 'constants/config';
import { AuthClaims } from 'hooks/ui/useAuthClaims';

type User = {
  _id: string;
  email?: string;
  fullName?: string;
  userType?: string;
};

const shouldRunLogrocketTracking = (user?: User) => {
  const isGreenSite =
    window.location.host.startsWith('www') ||
    window.location.host.startsWith('joinfurther.com');

  const isHeadlessChrome = navigator.userAgent.includes('HeadlessChrome');

  return (
    configuration.STAGE === 'production' &&
    (!isGreenSite || (isGreenSite && user)) &&
    !isHeadlessChrome
  );
};

const shouldRunSentryTracking = () => {
  return configuration.STAGE === 'production';
};

export const initLogrocket = (user?: User, authClaims?: AuthClaims) => {
  if (shouldRunLogrocketTracking(user)) {
    LogRocket.init('sq3uml/investor-site', {
      network: {
        requestSanitizer: (request) => {
          try {
            if (request.body) {
              const jsonBody = JSON.parse(request.body);
              if (jsonBody.password) {
                jsonBody.password = '***';
              }
              if (jsonBody.repeatpassword) {
                jsonBody.repeatpassword = '***';
              }
              if (jsonBody.newPassword) {
                jsonBody.newPassword = '***';
              }
              if (jsonBody.repeatNewPassword) {
                jsonBody.repeatNewPassword = '***';
              }
              request.body = JSON.stringify(jsonBody);
            }
            return request;
          } catch (_error) {
            return request;
          }
        },
      },
    });
    if (user) {
      LogRocket.identify(user._id, {
        ...(user.fullName && { name: user.fullName }),
        ...(user.email && { email: user.email }),
        ...(user.userType && { userType: user.userType }),
        ...(authClaims?.email && { furtherUserEmail: authClaims.email }),
        ...(authClaims?.furtherUserId && {
          furtherUserId: authClaims?.furtherUserId,
        }),
      });
    }
    setupLogRocketReact(LogRocket);

    LogRocket.getSessionURL((sessionURL) => {
      Sentry.setTag('logrocketSessionURL', sessionURL);
    });
  }
};

export const initSentry = () => {
  if (shouldRunSentryTracking()) {
    Sentry.init({
      dsn: 'https://0c60065c2f103c7980916c9819d4472b@o4504655840739328.ingest.us.sentry.io/4507017375449088',
      integrations: [Sentry.browserTracingIntegration()],
      tracesSampleRate: 0,
      replaysSessionSampleRate: 0,
      replaysOnErrorSampleRate: 0,
      environment: process.env.REACT_APP_STAGE,
      release: process.env.REACT_APP_VERSION_TAG,
    });
  }
};

export const identifySentry = (user: User, authClaims: AuthClaims) => {
  if (shouldRunSentryTracking()) {
    Sentry.setUser({
      id: user._id,
      name: user?.fullName,
      email: user.email,
      userType: user?.userType,
      furtherUserEmail: authClaims?.email,
      furtherUserId: authClaims?.furtherUserId,
    });
  }
};

export const maskRequestBody = (requestBody: string) => {
  return requestBody
    .replace(/"password":\s*"[^"]*"/g, '"password": "****"')
    .replace(/"repeatpassword":\s*"[^"]*"/g, '"repeatpassword": "****"')
    .replace(/"newPassword":\s*"[^"]*"/g, '"newPassword": "****"')
    .replace(/"repeatNewPassword":\s*"[^"]*"/g, '"repeatNewPassword": "****"');
};

export const identifyPosthog = (
  user: User,
  authClaims: AuthClaims,
  posthog: PostHog,
) => {
  posthog.identify(user._id, {
    name: user.fullName,
    email: user.email,
    userType: user.userType,
    furtherUserEmail: authClaims?.email,
    furtherUserId: authClaims?.furtherUserId,
  });
};
